import {
  getItemDef,
  type Inventory,
  type ItemInstance,
  type Vec3,
  type WeaponEquipSlot,
  type WeaponItemDef,
} from "@rietz/shared";
import type { RangeTarget } from "./layout.js";

/**
 * The shooting range's arithmetic: no three.js, no DOM, no store. Everything
 * takes `nowMs` and an rng seam (`pick: () => number` in [0,1)) and returns a
 * value, so the drill runs the same at a thousand test frames a second as at
 * sixty in the game, and which plate lights next can be asserted exactly. The
 * game hands both seams `Math.random` — nothing here is sent anywhere.
 *
 * `DrillState` is immutable: every function returns the state it was handed
 * (nothing changed) or a fresh one, so the caller compares by reference to
 * decide whether the lit plate moved.
 */

/** How long one timed drill runs, seconds. */
export const DRILL_SEC = 30;

/**
 * How long one plate stays lit before the drill moves on, seconds. Hitting it
 * re-lights immediately, so a fast shooter is paced by their own hands.
 */
export const DRILL_LIT_SEC = 2;

/**
 * One running drill. `seq` is bumped on every re-light so "the same plate lit
 * again" (a one-plate range) is distinguishable from "nothing happened".
 */
export interface DrillState {
  /** `performance.now()` at which the drill is over. */
  endsAtMs: number;
  /** The plate that scores right now. */
  litId: string;
  /** When the lit plate moves on by itself. */
  nextSwitchAtMs: number;
  score: number;
  seq: number;
}

/**
 * Opens a drill with a random plate lit. Empty `targetIds` gives `litId` `""`,
 * so nothing scores and the drill runs out.
 */
export function startDrill(
  targetIds: readonly string[],
  nowMs: number,
  pick: () => number,
): DrillState {
  return {
    endsAtMs: nowMs + DRILL_SEC * 1000,
    litId: pickAny(targetIds, pick),
    nextSwitchAtMs: nowMs + DRILL_LIT_SEC * 1000,
    score: 0,
    seq: 0,
  };
}

/**
 * One frame of a running drill: `null` once over, the SAME object while nothing
 * changed, a fresh one when the lit plate moves. The next switch is measured
 * from `nowMs`, so a late frame does not burn through missed switches.
 */
export function stepDrill(
  d: DrillState,
  targetIds: readonly string[],
  nowMs: number,
  pick: () => number,
): DrillState | null {
  if (nowMs >= d.endsAtMs) return null;
  if (nowMs < d.nextSwitchAtMs) return d;
  return {
    endsAtMs: d.endsAtMs,
    litId: pickOther(targetIds, d.litId, pick),
    nextSwitchAtMs: nowMs + DRILL_LIT_SEC * 1000,
    score: d.score,
    seq: d.seq + 1,
  };
}

/**
 * A round landed on `hitId`. On the LIT plate: a point, and the next plate
 * lights immediately — the cadence is the shooter's. Any other plate: nothing,
 * not even a penalty. Takes `targetIds` because re-lighting a DIFFERENT plate
 * needs the list.
 */
export function scoreHit(
  d: DrillState,
  targetIds: readonly string[],
  hitId: string,
  nowMs: number,
  pick: () => number,
): DrillState {
  if (hitId !== d.litId) return d;
  return {
    endsAtMs: d.endsAtMs,
    litId: pickOther(targetIds, d.litId, pick),
    nextSwitchAtMs: nowMs + DRILL_LIT_SEC * 1000,
    score: d.score + 1,
    seq: d.seq + 1,
  };
}

/**
 * Whole seconds left, never negative, rounded UP: published into the HUD every
 * frame, so it must not differ every frame; and "30" at the start, "0" exactly
 * at the end.
 */
export function drillSecLeft(d: DrillState, nowMs: number): number {
  const left = (d.endsAtMs - nowMs) / 1000;
  return left <= 0 ? 0 : Math.ceil(left);
}

/**
 * Which plate a round stopped in, or `null` for the wall. The hit is a point ON
 * the face, hence the tolerance. First match wins; plates do not overlap.
 */
export function hitTarget(
  targets: readonly RangeTarget[],
  pos: Readonly<Vec3>,
  toleranceM: number,
): RangeTarget | null {
  for (const t of targets) {
    const b = t.box;
    if (pos.x < b.min.x - toleranceM || pos.x > b.max.x + toleranceM) continue;
    if (pos.y < b.min.y - toleranceM || pos.y > b.max.y + toleranceM) continue;
    if (pos.z < b.min.z - toleranceM || pos.z > b.max.z + toleranceM) continue;
    return t;
  }
  return null;
}

/** Where the range looks for a gun, in order. The primary first, because that
 *  is the gun the player is building and the one they came down to test. */
const RANGE_SLOT_ORDER: readonly WeaponEquipSlot[] = ["weapon", "pistol", "weapon2"];

/**
 * The gun the kit takes to the range, or `null` for none (the range then hands
 * out a factory carbine — see `HideoutSession`). A slot holding a non-weapon is
 * skipped rather than refused.
 */
export function pickRangeWeapon(
  kit: Inventory,
): { slot: WeaponEquipSlot; item: ItemInstance; def: WeaponItemDef } | null {
  for (const slot of RANGE_SLOT_ORDER) {
    const item = kit.equipment[slot];
    if (item === null) continue;
    const def = getItemDef(item.defId);
    if (def === null || def.kind !== "weapon") continue;
    return { slot, item, def };
  }
  return null;
}

/**
 * Rotates unit vector `out` by `theta` in a uniformly random direction and
 * renormalises. A COPY of `FireControl.applyCone` with the rng as a parameter —
 * hoisting it into `shared/` would put a random seam into the deterministic
 * half. Allocation-free; once per pellet.
 */
export function deviate(out: Vec3, theta: number, rng: () => number): void {
  if (theta <= 0) return;
  const phi = rng() * Math.PI * 2;

  // An orthonormal basis around `out`. Up is (0,1,0) except when looking
  // (near) straight up or down, where that degenerates and −Z is used instead.
  let ux: number;
  let uy: number;
  let uz: number;
  if (Math.abs(out.y) > 0.999) {
    ux = 0;
    uy = 0;
    uz = -1;
  } else {
    ux = 0;
    uy = 1;
    uz = 0;
  }
  // right = normalize(dir × up)
  let rx = out.y * uz - out.z * uy;
  let ry = out.z * ux - out.x * uz;
  let rz = out.x * uy - out.y * ux;
  const rl = Math.hypot(rx, ry, rz);
  if (rl < 1e-6) return;
  rx /= rl;
  ry /= rl;
  rz /= rl;
  // up' = right × dir (already unit: both operands are unit and orthogonal)
  const bx = ry * out.z - rz * out.y;
  const by = rz * out.x - rx * out.z;
  const bz = rx * out.y - ry * out.x;

  const t = Math.tan(theta);
  const ox = (rx * Math.cos(phi) + bx * Math.sin(phi)) * t;
  const oy = (ry * Math.cos(phi) + by * Math.sin(phi)) * t;
  const oz = (rz * Math.cos(phi) + bz * Math.sin(phi)) * t;

  out.x += ox;
  out.y += oy;
  out.z += oz;
  const len = Math.hypot(out.x, out.y, out.z);
  if (len > 0) {
    out.x /= len;
    out.y /= len;
    out.z /= len;
  }
}

// ---------------------------------------------------------------------------

/** A plate, uniformly. `""` when there are none — see `startDrill`. */
function pickAny(ids: readonly string[], pick: () => number): string {
  if (ids.length === 0) return "";
  const i = Math.min(ids.length - 1, Math.max(0, Math.floor(pick() * ids.length)));
  return ids[i] as string;
}

/**
 * A plate that is NOT `exclude`, uniformly, drawn from the others DIRECTLY (one
 * `pick` per re-light, no reroll loop). With one plate or none the lit plate
 * stays — a one-plate range still runs a drill.
 */
function pickOther(ids: readonly string[], exclude: string, pick: () => number): string {
  if (ids.length === 0) return "";
  if (ids.length === 1) return ids[0] as string;

  // `exclude` need not be a candidate (a re-light after a layout change); then
  // every plate is fair game.
  let others = 0;
  for (const id of ids) if (id !== exclude) others += 1;
  if (others === 0) return ids[0] as string;

  const want = Math.min(others - 1, Math.max(0, Math.floor(pick() * others)));
  let n = 0;
  for (const id of ids) {
    if (id === exclude) continue;
    if (n === want) return id;
    n += 1;
  }
  return ids[0] as string;
}
